import { useEffect, useState } from "react";
import { Trans, useLingui } from "@lingui/macro";
import { Download, Loader2 } from "lucide-react";

import { Button } from "@app/components/ui/button";
import { Input } from "@app/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@app/components/ui/select";
import { cn } from "@app/lib/utils";
import type {
  ScoreDirection,
  StudyScore,
  StudyTest,
  WaterfallAnalysisRequest,
  WaterfallBar,
  WaterfallGroup,
  WaterfallResult,
} from "@app/types/study";

import { formatMeasure, formatNumber, formatP, formatPercent } from "./format";
import { groupColor } from "@app/lib/palette";
import { DEFAULT_WATERFALL } from "./state";
import { MethodsBox, WarningsList } from "./StudyNotes";
import { TestName } from "./TestLibrary";
import { TrajectoryChart } from "./TrajectoryChart";

interface Props {
  scores: StudyScore[] | null;
  settings: WaterfallAnalysisRequest | null;
  onSettings: (next: WaterfallAnalysisRequest) => void;
  result: WaterfallResult | null;
  loading: boolean;
  error: string | null;
  onExportCsv: () => void;
  exporting: boolean;
}

/** The moment a score is read at: the one asked when the score offers it, else a year, else its first. */
function momentOf(score: StudyScore | null, asked: number | null): number | null {
  const offered = score?.moments.map((m) => m.months) ?? [];
  if (asked !== null && offered.includes(asked)) return asked;
  if (offered.includes(DEFAULT_WATERFALL.horizon_months)) return DEFAULT_WATERFALL.horizon_months;
  return offered[0] ?? null;
}

function niceCeil(value: number): number {
  const power = 10 ** Math.floor(Math.log10(value));
  const step =
    [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find((m) => m * power >= value) ?? 10;
  return step * power;
}

/** The change counted the way the score improves — positive is better — or the raw change. */
function improvementOf(
  bar: WaterfallBar,
  better: ScoreDirection | null,
): number {
  return better === "lower" ? -bar.change : bar.change;
}

function Verdict({
  test,
  alpha,
  locale,
}: {
  test: StudyTest | null;
  alpha: number;
  locale: string;
}) {
  if (!test) return null;
  if (test.p === null) {
    return <span className="text-slate-400">{test.reason_text ?? "—"}</span>;
  }
  return (
    <span
      className={cn(
        "tabular-nums",
        test.p < alpha ? "font-semibold text-primary" : "text-slate-600",
      )}
    >
      {/* « p < 0,001 », never « p = < 0,001 ». */}
      {formatP(test.p, locale).startsWith("<") ? "p " : "p = "}
      {formatP(test.p, locale)}{" "}
      <span className="text-2xs font-normal text-slate-400">
        (
        <TestName
          code={test.code}
          name={test.name}
          reason={test.reason}
          reasonText={test.reason_text}
        />
        )
      </span>
    </span>
  );
}

/**
 * One group's bars, best improvement first, on the scale every panel shares. Improved patients —
 * past the threshold — are drawn at full weight, the others lighter: the distinction is weight, not
 * a hue, since green and red say a clinician's verdict.
 */
function Panel({
  group,
  index,
  better,
  threshold,
  domain,
  unit,
}: {
  group: WaterfallGroup;
  index: number;
  better: ScoreDirection | null;
  threshold: number | null;
  domain: [number, number];
  unit: string;
}) {
  const { i18n } = useLingui();
  const locale = i18n.locale;
  const [hovered, setHovered] = useState<number | null>(null);
  const bars = group.bars ?? [];
  const width = 760;
  const height = 180;
  const left = 44;
  const plotW = width - left - 8;
  const [low, high] = domain;
  const y = (v: number) => 8 + ((high - v) / (high - low || 1)) * (height - 24);
  const zero = y(0);
  const step = bars.length ? plotW / bars.length : plotW;
  // Thin marks with a visible gap, however many patients the group holds.
  const barW = Math.max(1, Math.min(14, step - 2));
  const ticks = [low, low / 2, 0, high / 2, high].filter(
    (v, i, all) => all.indexOf(v) === i,
  );
  const color = groupColor(index);
  const active = hovered === null ? null : bars[hovered];

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto w-full"
        role="img"
        aria-label={group.label}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={left}
              x2={width - 8}
              y1={y(tick)}
              y2={y(tick)}
              stroke={tick === 0 ? "#64748B" : "#E2E8F0"}
            />
            <text
              x={left - 6}
              y={y(tick)}
              dy="0.32em"
              textAnchor="end"
              fontSize="10"
              fill="#64748B"
            >
              {formatMeasure(tick, locale)}
            </text>
          </g>
        ))}
        {threshold !== null && better !== null && threshold <= high && (
          <line
            x1={left}
            x2={width - 8}
            y1={y(threshold)}
            y2={y(threshold)}
            stroke="#334155"
            strokeDasharray="4 3"
          />
        )}
        {bars.map((bar, i) => {
          const value = improvementOf(bar, better);
          const improved =
            threshold !== null && better !== null && value >= threshold;
          const top = Math.min(zero, y(value));
          return (
            <rect
              key={i}
              x={left + i * step + (step - barW) / 2}
              y={top}
              width={barW}
              height={Math.max(1, Math.abs(y(value) - zero))}
              rx={Math.min(2, barW / 2)}
              fill={color}
              fillOpacity={
                threshold === null || better === null || improved ? 1 : 0.4
              }
              stroke={hovered === i ? "#0F172A" : "none"}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            />
          );
        })}
      </svg>
      {active && hovered !== null && (
        <div
          className="pointer-events-none absolute top-1 z-10 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-2xs text-slate-700 shadow-md"
          style={{
            left: `${Math.min(80, ((left + hovered * step) / width) * 100)}%`,
          }}
        >
          <div className="tabular-nums">
            <Trans>
              {formatMeasure(active.before, locale)} {unit} avant →{" "}
              {formatMeasure(active.after, locale)} {unit} à{" "}
              {formatNumber(active.months, locale, 1)} mois
            </Trans>
          </div>
          <div className="font-semibold tabular-nums text-slate-900">
            <Trans>Variation : {formatMeasure(active.change, locale)}</Trans>
          </div>
        </div>
      )}
    </div>
  );
}

/** « Évolution d'un score »: a score before and after the act, one bar per operated episode. */
export function WaterfallAnalysis({
  scores,
  settings,
  onSettings,
  result,
  loading,
  error,
  onExportCsv,
  exporting,
}: Props) {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const score = scores?.find((s) => s.key === settings?.score) ?? null;
  const set = (patch: Partial<WaterfallAnalysisRequest>) => {
    if (!settings) return;
    onSettings({ ...settings, ...patch });
  };
  const declared = score?.better ?? null;
  const better = result?.score.better ?? declared ?? settings?.better ?? null;
  const horizon = momentOf(score, settings?.horizon_months ?? null);
  const moment = score?.moments.find((m) => m.months === horizon) ?? null;
  // A link saved before the calendar asked for a moment the score may not offer: read the one shown.
  useEffect(() => {
    if (settings && horizon !== null && horizon !== settings.horizon_months) {
      onSettings({ ...settings, horizon_months: horizon });
    }
  }, [settings, horizon, onSettings]);

  const allBars = (result?.groups ?? []).flatMap((g) =>
    (g.bars ?? []).map((bar) => improvementOf(bar, better)),
  );
  // Rounded up to a round step, so the axis reads 0 · 30 · 60 rather than 0 · 29,3 · 58,5.
  const extent = niceCeil(
    Math.max(1, ...allBars.map(Math.abs), result?.threshold ?? 0),
  );
  const domain: [number, number] = [
    Math.min(0, ...allBars) < 0 ? -extent : 0,
    Math.max(0, ...allBars) > 0 || (result?.threshold ?? 0) > 0 ? extent : 0,
  ];
  const named = (result?.groups ?? []).some((g) => g.bars !== null);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-4 rounded-xl border border-slate-200 bg-white p-4">
        <div className="space-y-1">
          <span className="block text-xs font-medium text-slate-500">
            <Trans>Score suivi</Trans>
          </span>
          <Select
            value={settings?.score ?? ""}
            onValueChange={(key) => {
              const next = { ...DEFAULT_WATERFALL, ...(settings ?? {}), score: key };
              const chosen = scores?.find((s) => s.key === key) ?? null;
              onSettings({
                ...next,
                horizon_months: momentOf(chosen, next.horizon_months) ?? next.horizon_months,
              });
            }}
            disabled={!scores || scores.length === 0}
          >
            <SelectTrigger className="w-72">
              <SelectValue placeholder={t`Choisir un score...`} />
            </SelectTrigger>
            <SelectContent>
              {(scores ?? []).map((s) => (
                <SelectItem key={s.key} value={s.key}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <span className="block text-xs font-medium text-slate-500">
            <Trans>Mesure après l'acte</Trans>
          </span>
          <div className="inline-flex rounded-md bg-slate-100 p-0.5">
            {(score?.moments ?? []).map((m) => (
              <button
                key={m.months}
                type="button"
                disabled={!settings}
                onClick={() => set({ horizon_months: m.months })}
                aria-pressed={horizon === m.months}
                className={cn(
                  "rounded px-2.5 py-1 text-xs font-medium transition-colors",
                  horizon === m.months
                    ? "bg-white text-primary shadow-sm"
                    : "text-slate-500 hover:text-slate-700",
                )}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-1">
          <span className="block text-xs font-medium text-slate-500">
            <Trans>Une amélioration, c'est</Trans>
          </span>
          {declared ? (
            <p className="py-1.5 text-sm text-slate-700">
              {declared === "lower"
                ? t`une baisse du score`
                : t`une hausse du score`}{" "}
              <span className="text-2xs text-slate-400">
                <Trans>(déclaré par le questionnaire)</Trans>
              </span>
            </p>
          ) : (
            <div className="inline-flex rounded-md bg-slate-100 p-0.5">
              {(["lower", "higher"] as const).map((direction) => (
                <button
                  key={direction}
                  type="button"
                  disabled={!settings}
                  onClick={() => set({ better: direction })}
                  aria-pressed={settings?.better === direction}
                  className={cn(
                    "rounded px-2.5 py-1 text-xs font-medium transition-colors",
                    settings?.better === direction
                      ? "bg-white text-primary shadow-sm"
                      : "text-slate-500 hover:text-slate-700",
                  )}
                >
                  {direction === "lower" ? t`une baisse` : t`une hausse`}
                </button>
              ))}
            </div>
          )}
        </div>

        <label className="space-y-1">
          <span className="block text-xs font-medium text-slate-500">
            <Trans>Seuil d'amélioration ({score?.unit || t`points`})</Trans>
          </span>
          <Input
            type="number"
            min={0}
            step="any"
            className="w-28"
            placeholder={t`Ex. : 10`}
            value={settings?.threshold ?? ""}
            disabled={!settings}
            onChange={(event) => {
              const raw = event.target.value;
              const next = Number(raw);
              set({
                threshold:
                  raw === "" || !Number.isFinite(next) || next < 0
                    ? null
                    : next,
              });
            }}
          />
        </label>

        <div className="ml-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={onExportCsv}
            disabled={!result || exporting}
            className="gap-1.5 hover:border-primary/50"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            <Trans>CSV</Trans>
          </Button>
        </div>

        <p className="w-full text-xs text-slate-500">
          {moment && (
            <Trans>
              Pour chaque épisode opéré : la dernière passation avant l'acte, et
              la passation la plus proche de {moment.label} après lui, au-delà de{" "}
              {formatNumber(moment.low, locale, 1)} et jusqu'à{" "}
              {formatNumber(moment.high, locale, 1)} mois, avant tout épisode
              suivant — les mêmes valeurs que les colonnes de l'export et les
              variables du tableau.
            </Trans>
          )}
        </p>
      </div>

      {scores && scores.length === 0 && (
        <p className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center text-sm text-slate-500">
          <Trans>
            Aucun score suivi dans le temps n'est déclaré pour cette spécialité.
          </Trans>
        </p>
      )}
      {scores && scores.length > 0 && !settings && (
        <p className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center text-sm text-slate-500">
          <Trans>
            Choisissez le score dont vous voulez suivre l'évolution.
          </Trans>
        </p>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
      {!result && loading && (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {result && (
        <div
          className={cn(
            "space-y-4 transition-opacity",
            loading && "opacity-60",
          )}
        >
          <WarningsList warnings={result.warnings} />

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-900">
              <Trans>Évolution aux temps de suivi</Trans>
            </h3>
            <p className="mb-3 text-xs text-slate-500">
              <Trans>
                Médiane [Q1 – Q3] des épisodes opérés renseignés à chaque temps : les
                effectifs baissent à mesure que le suivi s'allonge.
              </Trans>
            </p>
            <TrajectoryChart
              groups={result.groups}
              trajectory={result.trajectory}
              unit={result.score.unit}
              better={result.score.better}
              declaredRange={result.score.declared_range}
            />
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">
                    <Trans>Groupe</Trans>
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <Trans>Opérés</Trans>
                  </th>
                  <th className="px-3 py-2.5 text-right font-medium">
                    <Trans>Avant et après</Trans>
                  </th>
                  <th className="px-3 py-2.5 text-left font-medium">
                    <Trans>Variation médiane [Q1 – Q3]</Trans>
                  </th>
                  {result.threshold !== null && better !== null && (
                    <th className="px-3 py-2.5 text-right font-medium">
                      <Trans>
                        Améliorés ≥ {formatMeasure(result.threshold, locale)}
                      </Trans>
                    </th>
                  )}
                  <th className="px-3 py-2.5 text-left font-medium">
                    <Trans>Le score a-t-il changé ?</Trans>
                  </th>
                </tr>
              </thead>
              <tbody>
                {result.groups.map((group, index) => (
                  <tr key={group.id} className="border-t border-slate-200">
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-1.5 font-medium text-slate-900">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ background: groupColor(index) }}
                        />
                        {group.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {group.operated ?? "—"}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {group.paired ?? "—"}
                      <span className="block text-2xs text-slate-400">
                        <Trans>
                          {group.with_before ?? "—"} avant ·{" "}
                          {group.with_after ?? "—"} après
                        </Trans>
                      </span>
                    </td>
                    <td className="px-3 py-2.5 tabular-nums">
                      {group.median === null ? (
                        "—"
                      ) : (
                        <>
                          {formatMeasure(group.median, locale)}{" "}
                          <span className="text-slate-400">
                            [{formatMeasure(group.q1, locale)} ;{" "}
                            {formatMeasure(group.q3, locale)}]
                          </span>
                        </>
                      )}
                    </td>
                    {result.threshold !== null && better !== null && (
                      <td className="px-3 py-2.5 text-right tabular-nums">
                        {group.improved === null || !group.paired
                          ? "—"
                          : `${group.improved} (${formatPercent(group.improved, group.paired, locale)})`}
                      </td>
                    )}
                    <td className="px-3 py-2.5 text-xs">
                      <Verdict
                        test={group.test}
                        alpha={result.alpha}
                        locale={locale}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(result.comparison || result.improved_comparison) && (
            <div className="space-y-1 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
              {result.comparison && (
                <p>
                  <Trans>
                    Les variations diffèrent-elles entre les groupes ?
                  </Trans>{" "}
                  <Verdict
                    test={result.comparison}
                    alpha={result.alpha}
                    locale={locale}
                  />
                </p>
              )}
              {result.improved_comparison && (
                <p>
                  <Trans>La part de patients améliorés diffère-t-elle ?</Trans>{" "}
                  <Verdict
                    test={result.improved_comparison}
                    alpha={result.alpha}
                    locale={locale}
                  />
                </p>
              )}
            </div>
          )}

          {named && (
            <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs text-slate-500">
                {better === null ? (
                  <Trans>
                    Une barre par épisode opéré : la variation du score, de la
                    plus forte hausse à la plus forte baisse.
                  </Trans>
                ) : (
                  <Trans>
                    Une barre par épisode opéré : l'amélioration du score,
                    au-dessus de zéro quand le patient va mieux, de la plus
                    forte à la plus faible.
                  </Trans>
                )}
                {result.threshold !== null && better !== null && (
                  <>
                    {" "}
                    <Trans>
                      Pointillé : le seuil de{" "}
                      {formatMeasure(result.threshold, locale)} ; en plein, les
                      patients qui l'atteignent.
                    </Trans>
                  </>
                )}
              </p>
              {result.groups.map((group, index) => (
                <section key={group.id}>
                  <h4 className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-slate-900">
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ background: groupColor(index) }}
                    />
                    {group.label}
                  </h4>
                  {group.bars === null ? (
                    <p className="py-3 text-xs text-slate-500">
                      <Trans>
                        Cohorte lue en agrégé : seul le résumé ci-dessus est
                        publié.
                      </Trans>
                    </p>
                  ) : group.bars.length === 0 ? (
                    <p className="py-3 text-xs text-slate-500">
                      <Trans>
                        Aucun épisode opéré n'a une mesure avant et une mesure
                        après l'acte.
                      </Trans>
                    </p>
                  ) : (
                    <Panel
                      group={group}
                      index={index}
                      better={better}
                      threshold={result.threshold}
                      domain={domain}
                      unit={result.score.unit}
                    />
                  )}
                </section>
              ))}
            </div>
          )}

          <MethodsBox text={result.methods} />
        </div>
      )}
    </div>
  );
}
